package input

import com.soywiz.kmem.*
import com.soywiz.korge.input.*
import com.soywiz.korge.ui.*
import com.soywiz.korge.view.*
import com.soywiz.korim.color.*
import com.soywiz.korim.text.*
import com.soywiz.korma.geom.*
import com.soywiz.korma.geom.vector.*
import kotlin.math.*

fun Container.addScreenController(
    width: Double = 320.0,
    height: Double = 224.0,
    radius: Double = height / 8,
): InputControllerData {
    lateinit var ball: View
    val diameter = radius * 2

    val stickContainer = fixedSizeContainer(width = width / 2.0, height = diameter * 2) {
        position(0.0, height - this.height)
        val fixedContainerHeight = this.height
        container {
            position(radius * 1.5, fixedContainerHeight - radius * 1.5)
            graphics {
                fill(Colors.BLACK) {
                    circle(0.0, 0.0, radius)
                }
            }.alpha(0.2)
            ball = graphics {
                fill(Colors.WHITE) {
                    circle(0.0, 0.0, radius * 0.7)
                }
            }.alpha(0.2)
        }
    }

    val controller = OnScreenController(ball, width, radius)

    stickContainer.addStickBehaviour(radius, ball, controller)

    val jumpButtonView = jumpButton(width ,height, radius, controller)

    val firstAbilityButton = firstAbilityButton(width, height, radius, controller)
    val secondAbilityButton = secondAbilityButton(width, height, radius, controller)

    return InputControllerData(
        controller = controller,
        stick = ball,
        jumpButton = null,
        firstAbilityButton = firstAbilityButton,
        secondAbilityButton = secondAbilityButton,
        dashButton = null,
    )
}

private fun Container.addStickBehaviour(radius: Double, ball: View, controller: OnScreenController) = singleTouch {
    var dragging = false
    val start = Point(0, 0)

    start {
        start.x = it.globalX
        start.y = it.globalY
        ball.alpha = 0.3
        dragging = true
    }

    startAnywhere {
        if (it.globalX >= width / 2) return@startAnywhere
        start.x = it.globalX
        start.y = it.globalY
        ball.alpha = 0.3
        dragging = true
    }

    move {
        if (dragging) {
            val deltaX = it.globalX - start.x
            val deltaY = it.globalY - start.y
            val length = hypot(deltaX, deltaY)
            val maxLength = radius * 0.3
            val lengthClamped = length.clamp(0.0, maxLength)
            val angle = Angle.between(start.x, start.y, it.globalX, it.globalY)
            ball.position(cos(angle) * lengthClamped, sin(angle) * lengthClamped)
            val lengthNormalized = lengthClamped / maxLength
            controller.onStick(cos(angle) * lengthNormalized, sin(angle) * lengthNormalized)
        }
    }

    moveAnywhere {
        if (dragging) {
            val deltaX = it.globalX - start.x
            val deltaY = it.globalY - start.y
            val length = hypot(deltaX, deltaY)
            val maxLength = radius * 0.3
            val lengthClamped = length.clamp(0.0, maxLength)
            val angle = Angle.between(start.x, start.y, it.globalX, it.globalY)
            ball.position(cos(angle) * lengthClamped, sin(angle) * lengthClamped)
            val lengthNormalized = lengthClamped / maxLength
            controller.onStick(cos(angle) * lengthNormalized, sin(angle) * lengthNormalized)
        }
    }

    end {
        ball.position(0, 0)
        ball.alpha = 0.2
        dragging = false
        controller.onStick(0.0, 0.0)
    }

    endAnywhere {
        ball.position(0, 0)
        ball.alpha = 0.2
        dragging = false
        controller.onStick(0.0, 0.0)
    }
}

private fun Container.jumpButton(width: Double, height: Double, radius: Double, controller: ScreenController) = container {
    position(width - radius * 1.3, height - radius * 1.3)
    alpha(0.2)
    graphics {
        fill(Colors.WHITE) { circle(0.0, 0.0, radius * 0.7) }
    }

    singleTouch {
        var pressing = false
        start {
            pressing = true
            alpha = 0.3
            controller.onButton(OnScreenController.JUMP_BUTTON, true)
        }

        endAnywhere {
            if (pressing) {
                pressing = false
                alpha = 0.2
                controller.onButton(OnScreenController.JUMP_BUTTON, false)
            }
        }

        tap {
            if (pressing) {
                pressing = false
                alpha = 0.2
                controller.onButton(OnScreenController.JUMP_BUTTON, true)
            }
        }
    }
}

private fun Container.firstAbilityButton(width: Double, height: Double, radius: Double, controller: ScreenController): AbilityButton {
    val text = UIText("", width = radius, height = radius)
    val buttonContainer = container {
        position(width - radius * 1.3 - radius - radius * 0.5, height - radius * 1.3)
        alpha(0.2)
        graphics {
            fill(Colors.RED) { circle(0.0, 0.0, radius * 0.5) }
        }

        text.addTo(this@container).position(-(radius * 0.5), -(radius * 0.5)).apply {
            textAlignment = TextAlignment.CENTER
        }

        singleTouch {
            var pressing = false
            start {
                pressing = true
                alpha = 0.3
                controller.onButton(OnScreenController.ABILITY_BUTTON_1, true)
            }

            endAnywhere {
                if (pressing) {
                    pressing = false
                    alpha = 0.2
                    controller.onButton(OnScreenController.ABILITY_BUTTON_1, false)
                }
            }

            tap {
                if (pressing) {
                    pressing = false
                    alpha = 0.2
                    controller.onButton(OnScreenController.ABILITY_BUTTON_1, true)
                }
            }
        }
    }
    return AbilityButton(
        buttonContainer, text
    )
}

private fun Container.secondAbilityButton(width: Double, height: Double, radius: Double, controller: ScreenController): AbilityButton {
    val text = UIText("", width = radius, height = radius)
    val buttonContainer = container {
        position(width - radius * 1.3, height - radius * 1.3 - radius * 1.5)
        alpha(0.2)
        graphics {
            fill(Colors.ORANGE) { circle(0.0, 0.0, radius * 0.5) }
        }

        text.addTo(this@container).position(-(radius * 0.5), -(radius * 0.5)).apply {
            textAlignment = TextAlignment.CENTER
        }

        singleTouch {
            var pressing = false
            start {
                pressing = true
                alpha = 0.3
                controller.onButton(OnScreenController.ABILITY_BUTTON_2, true)
            }

            endAnywhere {
                if (pressing) {
                    pressing = false
                    alpha = 0.2
                    controller.onButton(OnScreenController.ABILITY_BUTTON_2, false)
                }
            }

            tap {
                if (pressing) {
                    pressing = false
                    alpha = 0.2
                    controller.onButton(OnScreenController.ABILITY_BUTTON_2, true)
                }
            }
        }
    }
    return AbilityButton(
        buttonContainer, text
    )
}

class AbilityButton(
    val container: Container,
    val text: UIText
) {
    var visibility: Boolean
        get() = container.visible
        set(value) {
            container.visible = value
        }
}

class OnScreenController(
    private val ball: View,
    private val width: Double,
    private val radius: Double,
) : ScreenController {

    override val left = KeyState()
    override val right = KeyState()
    override val jump = KeyState()
    override val down = KeyState()

    override val firstAbility = KeyState()
    override val secondAbility = KeyState()

    override val dash = KeyState()

    override fun onButton(buttonId: Int, pressed: Boolean) {
        when (buttonId) {
            JUMP_BUTTON -> {
                jump.changeStateByPressed(pressed)
            }

            ABILITY_BUTTON_1 -> {
                firstAbility.changeStateByPressed(pressed)
            }

            ABILITY_BUTTON_2 -> {
                secondAbility.changeStateByPressed(pressed)
            }

            else -> {
            }
        }
    }

    fun onStick(x: Double, y: Double) {
        if (x > 0.05) {
            right.changeStateByPressed(true)
        }

        if (x in -0.05..0.05) {
            right.changeStateByPressed(false)
            left.changeStateByPressed(false)
        }

        if (x < -0.1) {
            left.changeStateByPressed(true)
        }
    }

    companion object {
        const val JUMP_BUTTON = 1
        const val ABILITY_BUTTON_1 = 2
        const val ABILITY_BUTTON_2 = 3
    }
}
